// English (United States). Keys must match es-MX.js (checked by i18n.test.js).
const enUS = {
  app: {
    name: 'SmartLanded',
    descriptor: 'Landed cost calculator',
    language: 'Language'
  },
  common: {
    loading: 'Loading…',
    noData: 'No data.',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    open: 'Open',
    add: 'Add',
    remove: 'Remove',
    back: 'Back',
    yes: 'Yes',
    no: 'No',
    default: 'Default',
    all: 'All',
    total: 'Total',
    actions: 'Actions',
    status: 'Status',
    created: 'Created',
    rowsPerPage: 'Rows per page',
    totalCount: '{count} total',
    prev: 'Prev',
    next: 'Next',
    pageOf: 'Page {page} of {pages}',
    actionFailed: 'Action failed',
    none: '—'
  },
  nav: {
    dashboard: 'Dashboard',
    shipments: 'Shipments',
    users: 'Users',
    tariffs: 'Tariffs',
    help: 'Help',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    signOut: 'Sign out'
  },
  roles: {
    system_admin: 'System admin',
    client: 'Client'
  },
  login: {
    subtitle: 'Sign in to continue',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    failed: 'Invalid credentials',
    noAccount: "Don't have an account?",
    startTrial: 'Try it free',
    forgot: 'Forgot your password?'
  },
  forgot: {
    subtitle: "Enter your email and we'll send you a code to set a new password.",
    send: 'Send code',
    sending: 'Sending…',
    sendFailed: 'Could not send the code',
    sentTo: 'If there is an account with that email, we sent a code to',
    noAccountHint: 'Not your email?',
    changeEmail: 'Change it',
    newPassword: 'New password',
    submit: 'Save and sign in',
    done: 'Done. Your password was updated.',
    resetFailed: 'Could not change the password',
    backToLogin: 'Back to sign in'
  },
  signup: {
    subtitle: 'Create your account and run your first shipments for free.',
    perkShipments: '3 free shipments',
    perkCard: 'No credit card',
    perkSetup: 'Ready in 2 minutes',
    firstName: 'First name',
    lastName: 'Last name',
    company: 'Company',
    email: 'Work email',
    emailHint: "We'll send you a code to verify it.",
    password: 'Password',
    passwordHint: 'At least {min} characters.',
    passwordWeak: 'Too short',
    passwordOk: 'Fair',
    passwordStrong: 'Strong',
    acceptPrefix: 'I accept the',
    terms: 'Terms and Conditions',
    and: 'and the',
    privacy: 'Privacy Notice',
    submit: 'Create account',
    submitting: 'Creating account…',
    failed: 'Could not create the account',
    emailTaken: 'An account with this email already exists. Sign in.',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in'
  },
  verify: {
    sentTo: 'We sent a 6-digit code to',
    enterCode: 'Enter the code',
    digit: 'Digit {n}',
    expires: 'The code expires in 15 minutes.',
    submit: 'Verify and continue',
    verifying: 'Verifying…',
    failed: 'Could not verify the code',
    welcome: 'All set! Your account is active.',
    alreadyVerified: 'Your email was already verified. Sign in.',
    noCode: "Didn't get it?",
    resend: 'Resend code',
    resendIn: 'Resend in {seconds}s',
    resent: 'We sent you a new code',
    resentOnLogin: 'Your email is not verified yet; we sent you a new code',
    resendFailed: 'Could not resend the code',
    wrongEmail: 'Wrong email?',
    signupAgain: 'Sign up again'
  },
  trial: {
    label: 'Free trial',
    remaining_one: '{count} of {limit} free shipments left',
    remaining_other: '{count} of {limit} free shipments left',
    body: 'Each shipment includes reading its documents and the cost per SKU.',
    used: '{used} of {limit} used',
    reachedTitle: 'You used your {limit} free shipments',
    reachedBody: 'Your shipments and results stay available. Write to us to keep calculating.',
    dialogBody: "To create more shipments you need a plan. Write to us and we'll help you pick the right one.",
    keepData: 'You keep all your shipments and results.',
    fastAnswer: 'We answer within one business day.',
    talkToSales: 'Talk to sales',
    later: 'Not now',
    mailSubject: 'Plan for {company}',
    mailBody: "Hi, I'm {name} ({email}). I finished my free trial and want to keep using SmartLanded."
  },
  onboarding: {
    title: 'Get started in 3 steps',
    subtitle: 'Your first landed cost per SKU in a few minutes.',
    createTitle: 'Create a shipment',
    createBody: 'Give it a reference; the rest is filled from the documents.',
    uploadTitle: 'Upload its documents',
    uploadBody: 'Invoice, packing list, freight, broker account or pedimento: PDF, image or XML.',
    reviewTitle: 'Review and calculate',
    reviewBody: 'Confirm the suggested lines and get the unit cost per SKU.',
    cta: 'Create my first shipment'
  },
  guide: {
    title: 'Your shipment guide',
    progress: '{done} of {total} steps',
    hide: 'Hide guide',
    manual: 'See the full guide',
    doneTitle: 'Done: you have your landed cost per SKU',
    doneBody: 'Review the breakdown and export it to CSV or JSON whenever you want.',
    doneAction: 'Got it',
    steps: {
      documents: {
        title: 'Upload the documents',
        hint: 'Commercial invoice, packing list, freight, broker account or pedimento. They are classified and read for you.',
        action: 'Go to Documents'
      },
      items: {
        title: 'Review the SKUs',
        hint: 'They come from the commercial invoice. If you did not upload it, upload it or add the SKUs by hand.',
        action: 'Go to SKUs'
      },
      costs: {
        title: 'Confirm the costs',
        hint: 'Costs come from your documents: confirm, edit or reject each suggested line.',
        action: 'Go to Costs'
      },
      result: {
        title: 'Calculate',
        hint: 'All reviewed. Calculate to get the unit cost per SKU.',
        hintErrors: 'The calculation has errors: fix them and calculate again.',
        action: 'Calculate'
      }
    }
  },
  plans: {
    trial: 'Trial',
    paid: 'Paid'
  },
  status: {
    draft: 'Draft',
    review: 'Review',
    calculated: 'Calculated',
    closed: 'Closed',
    processing: 'Processing',
    ready: 'Ready',
    failed: 'Failed',
    suggested: 'Suggested',
    confirmed: 'Confirmed',
    rejected: 'Rejected',
    duplicate: 'Duplicate'
  },
  layers: {
    merchandise: 'Merchandise',
    origin_logistics: 'Origin logistics',
    international_freight: 'Int’l freight',
    insurance: 'Insurance',
    destination_logistics: 'Destination logistics',
    customs_broker: 'Customs broker',
    duties_taxes: 'Duties & taxes',
    final_delivery: 'Final delivery',
    other: 'Other'
  },
  docTypes: {
    commercial_invoice: 'Commercial invoice',
    packing_list: 'Packing list',
    freight_invoice: 'Freight invoice',
    forwarder_quote: 'Forwarder quote',
    customs_broker_account: 'Customs broker account',
    pedimento: 'Pedimento',
    insurance: 'Insurance',
    other: 'Other'
  },
  transport: {
    sea_fcl: 'Sea FCL',
    sea_lcl: 'Sea LCL',
    air: 'Air',
    land: 'Land'
  },
  methods: {
    ai: 'AI',
    cfdi_xml: 'CFDI XML',
    manual: 'Manual'
  },
  modes: {
    actual: 'Actual',
    estimate: 'Estimate',
    actualLong: 'Actual (pedimento)',
    estimateLong: 'Estimate'
  },
  bases: {
    value: 'value',
    weight: 'weight',
    volume: 'volume',
    quantity: 'quantity',
    duty: 'duty'
  },
  concepts: {
    PICKUP: 'Pickup / inland freight at origin',
    ORIGIN_HANDLING: 'Origin handling / THC',
    EXPORT_CLEARANCE: 'Export customs clearance',
    VGM: 'VGM / weighing',
    OCEAN_FREIGHT: 'Ocean freight',
    AIR_FREIGHT: 'Air freight',
    ROAD_FREIGHT_INTL: 'International road freight',
    BAF: 'Bunker / fuel surcharge (BAF/FAF)',
    CAF: 'Currency adjustment (CAF)',
    PSS: 'Peak season surcharge (PSS)',
    FREIGHT_SURCHARGE: 'Other freight surcharge',
    INSURANCE: 'Cargo insurance',
    DEST_THC: 'Destination THC / port handling',
    DECONSOLIDATION: 'Deconsolidation (LCL)',
    STORAGE: 'Storage',
    DEMURRAGE: 'Demurrage',
    DETENTION: 'Container detention',
    BL_RELEASE: 'B/L release / documentation',
    INSPECTION: 'Inspection / reconocimiento',
    BROKER_FEE: 'Customs broker fee',
    BROKER_SERVICES: 'Broker complementary services',
    IGI: 'Import duty (IGI)',
    DTA: 'Customs processing fee (DTA)',
    IVA: 'Import VAT (IVA)',
    PRV: 'Prevalidation (PRV)',
    IEPS: 'Special tax (IEPS)',
    CC: 'Countervailing duty',
    LAST_MILE: 'Delivery to warehouse',
    OTHER: 'Other'
  },
  issues: {
    subject: {
      item: 'Item "{name}"',
      line: 'Cost line "{name}"',
      vat: 'VAT of "{name}"'
    },
    NO_ITEMS: 'The shipment has no items (SKUs)',
    INVALID_AMOUNT: '{subject}: amount is missing or not a number',
    NO_EXCHANGE_RATE: '{subject}: no exchange rate {from} → {to}',
    NO_INCOTERM: 'No valid incoterm: seller-covered costs cannot be checked',
    ALLOCATION_FALLBACK: 'Allocation by {basis} needs it on every SKU; fell back to value',
    ESTIMATE_IGNORES_TAX_LINE: 'Estimate mode computes taxes; ignored tax line "{name}"',
    SELLER_COVERED: '"{name}" is {category}, which the seller covers under {incoterm}: check it is not counted twice',
    UNCONFIRMED_LINES_one: '1 cost line is still suggested (not confirmed)',
    UNCONFIRMED_LINES_other: '{count} cost lines are still suggested (not confirmed)',
    NO_TAXES: 'No duties/taxes found: upload the pedimento or switch to estimate mode',
    NO_IGI_RATE: 'SKU "{name}" has no IGI rate: assumed 0%',
    PRV_NOT_CONFIGURED: 'PRV is not configured in the tax profile: excluded from the estimate',
    FX_LOOKUP_FAILED: 'No Banxico rate for {currency}: {reason}'
  },
  fx: {
    sources: {
      manual: 'typed by hand',
      pedimento: 'from pedimento',
      banxico: 'Banxico {series}'
    },
    asOf: 'rate of {date}',
    errors: {
      FX_NOT_CONFIGURED: 'the Banxico token is not configured (BANXICO_TOKEN)',
      FX_UNSUPPORTED_CURRENCY: 'Banxico has no series configured for this currency',
      FX_UNAVAILABLE: 'Banxico is not available right now',
      FX_NO_DATA: 'Banxico has no rate for that date'
    }
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Shipments, pending review and what the landed cost is made of.',
    shipments: 'Shipments',
    calculated: 'Calculated',
    toReview: 'Cost lines to review',
    documents: 'Documents read',
    whereTitle: 'Where the landed cost goes',
    whereSubtitle: 'Share of each layer over all calculated shipments.',
    noneCalculated: 'No calculated shipments yet.',
    goToShipments: 'Go to shipments',
    ofLanded: '{value}% of landed cost',
    loadFailed: 'Could not load the dashboard'
  },
  adminDashboard: {
    title: 'Platform dashboard',
    subtitle: 'Platform health and activity per client.',
    clients: 'Clients',
    users: 'Users',
    shipments: 'Shipments',
    byClient: 'By client',
    client: 'Client',
    calculated: 'Calculated',
    documents: 'Documents',
    noClients: 'No clients yet.'
  },
  clientLifecycle: {
    active: 'Active',
    terminated: 'Terminated',
    purged: 'Data deleted',
    lastActivity: 'Last activity: {date}',
    noticeSent: 'Inactivity notice sent · closes on {date}',
    deletesOn: 'Its data is deleted on {date}',
    canExport: 'Its users can still sign in and export.',
    purgedOn: 'Deleted on {date}',
    purgedInactive: 'Trial closed for inactivity · deleted on {date}',
    terminate: 'Terminate client',
    reactivate: 'Reactivate (cancel deletion)',
    dialogTitle: 'Terminate {name}?',
    dialogExport: 'Its users will have {days} days to export their results (until {date}).',
    dialogDelete: 'On {date} its shipments, documents and users are deleted for good. This cannot be undone.',
    dialogUndo: 'Until then you can reactivate it and nothing is lost.',
    dialogKeep: 'We only keep what the law requires: name, plan, dates and the terms acceptance.',
    dialogTypeName: 'To confirm, type the client name: {name}',
    dialogConfirm: 'Terminate client',
    terminatedToast: '{name} terminated · its data is deleted on {date}',
    reactivatedToast: '{name} reactivated · deletion cancelled',
    failed: 'Could not update the client'
  },
  adminUsers: {
    title: 'Users & clients',
    subtitle: 'Create clients (tenants) and manage their users.',
    newClient: 'New client',
    clientName: 'Client name',
    client: 'Client',
    users: 'Users',
    shipments: 'Shipments',
    noClients: 'No clients yet.',
    createUser: 'Create user',
    editUser: 'Edit user',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    newPassword: 'New password (optional)',
    password: 'Password (min 8)',
    selectClient: 'Select client…',
    active: 'Active',
    saveChanges: 'Save changes',
    name: 'Name',
    role: 'Role',
    noUsers: 'No users yet.',
    activate: 'Activate',
    deactivate: 'Deactivate',
    confirmDelete: 'Delete user {email}?',
    loadFailed: 'Could not load users/clients',
    clientCreated: 'Client created',
    clientFailed: 'Could not create client',
    userCreated: 'User created',
    userUpdated: 'User updated',
    userDeleted: 'User deleted',
    userActivated: 'User activated',
    userDeactivated: 'User deactivated',
    saveFailed: 'Could not save the user',
    deleteFailed: 'Could not delete the user',
    updateFailed: 'Could not update the user',
    plan: 'Plan',
    shipmentLimit: 'Shipment limit',
    unlimited: 'Unlimited',
    usageOf: '{used} of {limit} shipments',
    usageUnlimited: '{used} shipments · unlimited',
    selfSignup: 'Self-service signup',
    planUpdated: 'Plan updated',
    planFailed: 'Could not update the plan'
  },
  shipments: {
    title: 'Shipments',
    subtitle: 'Each shipment groups its documents; AI reads them and the engine computes the cost per SKU.',
    new: 'New shipment',
    create: 'Create shipment',
    creating: 'Creating…',
    created: 'Shipment created',
    createFailed: 'Could not create the shipment',
    loadFailed: 'Could not load shipments',
    reference: 'Reference',
    referencePlaceholder: 'e.g. PO-4512 / BL MSCU1234567',
    incoterm: 'Incoterm',
    fromInvoice: 'From invoice',
    transport: 'Transport',
    mode: 'Mode',
    usdRate: 'USD → MXN',
    usdRatePlaceholder: 'From pedimento',
    search: 'Search reference',
    docs: 'Docs',
    skus: 'SKUs',
    toReview: 'To review',
    landedCost: 'Landed cost',
    empty: 'No shipments yet.',
    confirmDelete: 'Delete shipment {reference}?',
    deleted: 'Shipment deleted',
    deleteFailed: 'Could not delete'
  },
  shipment: {
    back: '← Shipments',
    notFound: 'Shipment not found.',
    loadFailed: 'Could not load the shipment',
    noIncoterm: 'No incoterm',
    noTransport: 'Transport —',
    settings: 'Settings',
    hideSettings: 'Hide settings',
    exportCsv: 'Export CSV',
    exportJson: 'Export JSON',
    exportFailed: 'Could not export',
    calculate: 'Calculate',
    recalculate: 'Recalculate',
    calculating: 'Calculating…',
    calculated: 'Landed cost calculated',
    calculatedWithErrors: 'Calculated with errors — see the Result tab',
    calculateFailed: 'Calculation failed',
    pending_one: '1 cost line suggested by extraction is waiting for review.',
    pending_other: '{count} cost lines suggested by extraction are waiting for review.',
    reviewCosts: 'Review costs',
    stale: 'The data changed since the last calculation — recalculate to refresh the result.',
    tabs: {
      documents: 'Documents',
      items: 'SKUs',
      costs: 'Costs',
      result: 'Result'
    }
  },
  settings: {
    reference: 'Reference',
    incoterm: 'Incoterm',
    transport: 'Transport',
    mode: 'Mode',
    reportCurrency: 'Report currency',
    vatRecoverable: 'The importer credits VAT (IVA is reported apart, not as cost)',
    ratesTitle: 'Exchange rates → {currency}',
    ratesHelp: 'Used when a line or SKU has no rate of its own. The pedimento fills USD; missing ones are fetched from Banxico. Rates typed here are never replaced.',
    addRate: '+ Add rate',
    rateDate: 'Exchange rate date',
    rateDateHelp: 'Entry or payment date. Empty = today.',
    refresh: 'Update from Banxico',
    refreshing: 'Updating…',
    refreshed_one: '1 rate updated from Banxico',
    refreshed_other: '{count} rates updated from Banxico',
    refreshNothing: 'Nothing to update: the rates are typed by hand or from the pedimento',
    refreshFailed: '{currency}: {reason}',
    allocationTitle: 'Allocation per layer',
    allocationHelp: 'How each layer is spread across SKUs. Default: the concept catalog.',
    save: 'Save settings',
    saved: 'Settings saved',
    saveFailed: 'Could not save'
  },
  documents: {
    pickFiles: 'Pick one or more files first',
    upload: 'Upload & extract',
    uploading: 'Reading documents…',
    uploadHelp: 'Commercial invoice, packing list, freight invoices, customs broker account, pedimento, insurance policy. PDFs and photos are read with AI; a CFDI XML is read directly, without AI.',
    processed_one: '1 document processed',
    processed_other: '{count} documents processed',
    failedCount_one: '1 document failed to extract',
    failedCount_other: '{count} documents failed to extract',
    uploadFailed: 'Upload failed',
    confirmDelete: 'Delete {file}? The SKUs and cost lines it produced are removed too.',
    deleted: 'Document deleted',
    deleteFailed: 'Could not delete',
    file: 'File',
    type: 'Type',
    method: 'Method',
    confidence: 'Confidence',
    uploaded: 'Uploaded',
    unclassified: 'Unclassified',
    empty: 'No documents yet.'
  },
  items: {
    help: 'SKUs come from the commercial invoice; the packing list fills weight and volume. IGI % is only used in estimate mode.',
    missingBasis: 'Some SKUs have no weight or volume: lines spread by those will fall back to value.',
    needsIgi: 'Estimate mode: SKUs without IGI % are estimated at 0%.',
    empty: 'No SKUs yet — upload the commercial invoice or add them by hand.',
    add: '+ Add SKU',
    saveFailed: 'Could not save the SKU',
    confirmDelete: 'Delete SKU {name}?',
    deleteFailed: 'Could not delete',
    columns: {
      sku: 'SKU',
      description: 'Description',
      quantity: 'Qty',
      amount: 'Amount',
      currency: 'Cur.',
      tariffFraction: 'HS code',
      igiRate: 'IGI %',
      grossWeightKg: 'Kg',
      volumeCbm: 'CBM'
    }
  },
  costs: {
    help: 'Lines proposed by extraction. Confirm, correct or reject; duplicates (e.g. taxes the broker account bills again) are not added.',
    confirmAll: 'Confirm all ({count})',
    add: 'Add cost line',
    confirmed: 'Lines confirmed',
    saved: 'Cost line saved',
    amountRequired: 'Amount and currency are required',
    empty: 'No cost lines yet.',
    concept: 'Concept',
    source: 'Source',
    amount: 'Amount',
    vat: 'VAT',
    flags: 'Flags',
    conf: 'Conf.',
    manual: 'Manual',
    unrecognised: 'Unrecognised concept',
    paidOnBehalf: 'paid on behalf',
    duplicateOf: 'duplicate of "{name}" ({source})',
    flagCustomsValue: 'customs value',
    flagRecoverable: 'recoverable',
    flagBy: 'by {basis}',
    confirm: 'Confirm',
    keep: 'Keep',
    reject: 'Reject',
    confirmDelete: 'Delete this cost line?',
    form: {
      concept: 'Concept',
      freeText: '— free text —',
      layer: 'Layer',
      description: 'Description',
      amount: 'Amount (before VAT)',
      currency: 'Currency',
      exchangeRate: 'Exchange rate',
      exchangeRatePlaceholder: 'shipment',
      vat: 'VAT',
      spreadBy: 'Spread by',
      inCustomsValue: 'In customs value',
      recoverable: 'Recoverable VAT'
    }
  },
  result: {
    notCalculated: 'Not calculated yet — review the costs and press Calculate.',
    errors: 'Errors — the result is incomplete',
    warnings: 'Warnings',
    landedCost: 'Landed cost',
    calculatedAt: 'Calculated {date} · {mode}',
    overMerchandise: 'Over merchandise',
    overMerchandiseHint: 'Logistics, broker and duties on top of the goods',
    customsValue: 'Customs value',
    customsValueHint: 'Merchandise + incrementables',
    recoverableVat: 'Recoverable VAT',
    recoverableVatHint: 'Cash flow, not cost',
    chartTitle: 'Unit cost per SKU by layer ({currency})',
    chartSubtitle: 'Each bar is one unit of the SKU, split into what it is made of.',
    sku: 'SKU',
    qty: 'Qty',
    total: 'Total',
    unitCost: 'Unit cost',
    taxesEstimated: 'Duties & taxes (estimated)',
    taxesActual: 'Duties & taxes (from pedimento)',
    ratesUsed: 'Exchange rates used',
    comparison: {
      title: 'Estimate vs actual',
      subtitle: 'What the estimate (tax profile + tariff) would have said, against the pedimento. Positive = actual cost higher.',
      concept: 'Concept',
      estimate: 'Estimate',
      actual: 'Actual',
      diff: 'Difference',
      landedCost: 'Landed cost',
      unitCostTitle: 'Unit cost per SKU',
      estimateWarnings: 'The estimate had warnings:'
    }
  },
  tariffs: {
    title: 'Tariff rates (IGI)',
    subtitle: 'General IGI rate by fracción arancelaria. SKUs with a fracción and no IGI % take it from here.',
    importTitle: 'Import CSV',
    importHelp: 'Export the tariff (SNICE / LIGIE) to CSV with a header naming the fracción, description and rate columns. Rates in percent: 10, 10%, Ex. Existing fracciones are updated.',
    import: 'Import',
    importing: 'Importing…',
    imported: '{count} rates imported',
    importErrors_one: '1 row skipped',
    importErrors_other: '{count} rows skipped',
    importFailed: 'Could not import the file',
    pickFile: 'Pick a CSV file first',
    search: 'Search fracción or description',
    fraction: 'Fracción',
    description: 'Description',
    rate: 'IGI',
    source: 'Source',
    updated: 'Updated',
    empty: 'No tariff rates yet — import a CSV.',
    confirmDelete: 'Delete the rate of {fraction}?',
    deleted: 'Rate deleted',
    deleteFailed: 'Could not delete',
    loadFailed: 'Could not load tariff rates'
  },
  document: {
    back: '← Shipment',
    notFound: 'Document not found.',
    loadFailed: 'Could not load the document',
    unclassified: 'Unclassified',
    extractionConfidence: 'extraction confidence {value}',
    typePlaceholder: '— Document type —',
    reextract: 'Re-extract',
    working: 'Working…',
    loadingFile: 'Loading file…',
    noPreview: 'No preview for {type}.',
    download: 'Download',
    extracted: 'Extracted data',
    fromTemplate: 'Start from template',
    emptyWithType: 'No data extracted. Start from the template or paste JSON.',
    emptyNoType: 'Pick the document type first.',
    saveHelp: 'Saving rebuilds the SKUs or cost lines of this document.',
    invalidJson: 'Invalid JSON: {error}',
    saved: 'Saved — SKUs and cost lines re-mapped',
    saveFailed: 'Could not save',
    extracted_ok: 'Extraction finished',
    extractFailed: 'Extraction failed'
  }
}

export default enUS
